import {useFocusManagement} from '@aboutyou/a11y';
import {Fade, Slide} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {FC, PropsWithChildren, SyntheticEvent, useCallback} from 'react';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {ContentPopperHeader} from '../ContentPopper/ContentPopperHeader';
import {
  CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
  CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
} from '../ContentPopper/utils/contentPopperAnimationDurations';
import {RenderInPortal} from '../RenderInPortal/RenderInPortal';

const disableOtherEvents = (e: SyntheticEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

interface ModalProps extends Omit<ContentPopperBaseProps, 'hideCloseButton'> {
  onModalClick?: VoidFunction | React.MouseEventHandler;
  onUnmount?: VoidFunction;
  backgroundBlur?: boolean;
  zIndex?: Z_INDEXES;
  preventScrollLock?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  'data-testid': dataTestId = 'modal',
  bodyContent,
  onClose,
  onModalClick,
  renderInPortal = false,
  open,
  onUnmount,
  preventAnimation = false,
  backgroundBlur = false,
  zIndex = Z_INDEXES.MODAL_DIALOG_WRAPPER,
  preventSlideUpAnimation,
  preventScrollLock = false,
  headerContent,
  onGoBack,
  popoverPriority,
  ...rest
}) => {
  const {focusManagementRefCallback, isTopMostPopover} = useFocusManagement({
    isOpen: open,
    priority: popoverPriority,
  });
  useKeyPress({
    key: 'Escape',
    onKeyDown: onClose,
    disabled: !open || isTopMostPopover === false,
  });
  useLockBodyScroll({lockScroll: preventScrollLock ? false : open});

  const onCloseModal = useCallback(
    (e: SyntheticEvent) => {
      disableOtherEvents(e);
      onClose?.();
    },
    [onClose],
  );

  const content = (
    <FadeAnimation
      open={open}
      preventAnimation={preventAnimation}
      onUnmount={onUnmount}
    >
      <BlurredOverlay
        onClick={onModalClick}
        onScroll={disableOtherEvents}
        aria-describedby="modalContent"
        aria-modal="true"
        data-testid={dataTestId}
        role="dialog"
        tabIndex={-1}
        $zIndex={zIndex}
        $enableBlur={backgroundBlur}
        {...rest}
      >
        <BodyAnimation
          open={open}
          preventAnimation={preventAnimation}
          bodyContent={
            <InnerWrapper>
              <FocusManagement ref={focusManagementRefCallback()} tabIndex={0}>
                {headerContent && (
                  <ModalHeader
                    data-testid={dataTestId}
                    onClose={onCloseModal}
                    onGoBack={onGoBack}
                  >
                    {headerContent}
                  </ModalHeader>
                )}
                <BodyContentWrapper>{bodyContent}</BodyContentWrapper>
              </FocusManagement>
            </InnerWrapper>
          }
          preventSlideUpAnimation={preventSlideUpAnimation}
        />
      </BlurredOverlay>
    </FadeAnimation>
  );

  if (renderInPortal) {
    return (
      <RenderInPortal portalId={'modal-portal-host'}>{content}</RenderInPortal>
    );
  }

  return content;
};

const BodyAnimation: FC<
  Pick<
    ModalProps,
    'bodyContent' | 'preventAnimation' | 'preventSlideUpAnimation' | 'open'
  >
> = ({bodyContent, preventAnimation, preventSlideUpAnimation, open}) => {
  if (preventAnimation || preventSlideUpAnimation) {
    return bodyContent;
  }

  return (
    <Slide mounted={open} direction="up" duration={300} transformLength={5}>
      {bodyContent}
    </Slide>
  );
};

const FadeAnimation: FC<
  PropsWithChildren<Pick<ModalProps, 'preventAnimation' | 'open' | 'onUnmount'>>
> = ({children, preventAnimation, open, onUnmount}) => {
  if (preventAnimation) {
    return <>{children}</>;
  }

  return (
    <Fade
      mounted={open}
      duration={{
        mount: CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
        unmount: CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
      }}
      onUnmount={onUnmount}
    >
      {children}
    </Fade>
  );
};

const InnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;

  ${tabletAndUpLinaria(`
    height: unset;
    width: unset;
  `)}
`;

const FocusManagement = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${tabletAndUpLinaria(`
    height: unset;
    width: unset;
  `)}
`;

const BodyContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

export const ModalHeader = styled(ContentPopperHeader)``;
